import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api from '../api';

const QuizCreatePage = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const { id } = useParams(); // For editing existing quiz
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        questions: [{
            questionText: '',
            questionType: 'multiple-choice',
            options: [{ text: '', isCorrect: false }],
            correctAnswer: '',
            points: 1,
            explanation: ''
        }],
        visibleToClasses: [],
        timeLimit: '',
        showResults: true,
        showCorrectAnswers: true
    });

    useEffect(() => {
        fetchClasses();
        if (id) {
            setIsEditing(true);
            fetchQuiz(id);
        }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            // Handle paginated response structure
            if (res.data.success && res.data.classes) {
                setClasses(res.data.classes.data || []);
            } else if (Array.isArray(res.data)) {
                setClasses(res.data);
            } else {
                setClasses([]);
            }
        } catch (error) {
            console.error('Error fetching classes:', error);
            setClasses([]);
        }
    };

    const fetchQuiz = async (quizId) => {
        try {
            setLoading(true);
            const res = await api.get(`/quizzes/${quizId}`);
            const quiz = res.data;

            setFormData({
                title: quiz.title,
                description: quiz.description || '',
                questions: quiz.questions.map(q => ({
                    ...q,
                    questionImage: q.questionImage || '',
                    options: q.options.length > 0 ? q.options : [{ text: '', isCorrect: false }]
                })),
                visibleToClasses: quiz.visibleToClasses.map(c => c._id),
                timeLimit: quiz.timeLimit || '',
                showResults: quiz.showResults,
                showCorrectAnswers: quiz.showCorrectAnswers
            });
        } catch (error) {
            console.error('Error fetching quiz:', error);
            alert('Error loading quiz data');
            navigate('/admin');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            const submitData = {
                ...formData,
                timeLimit: formData.timeLimit ? parseInt(formData.timeLimit) : null
            };

            if (isEditing) {
                await api.put(`/quizzes/${id}`, submitData);
                alert('Quiz updated successfully!');
            } else {
                await api.post('/quizzes', submitData);
                alert('Quiz created successfully!');
            }

            navigate('/admin');
        } catch (error) {
            console.error('Error saving quiz:', error);
            alert('Error saving quiz. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const addQuestion = () => {
        setFormData({
            ...formData,
            questions: [
                ...formData.questions,
                {
                    questionText: '',
                    questionType: 'multiple-choice',
                    questionImage: '',
                    options: [{ text: '', isCorrect: false }],
                    correctAnswer: '',
                    points: 1,
                    explanation: ''
                }
            ]
        });
    };

    const removeQuestion = (index) => {
        if (formData.questions.length === 1) return;
        setFormData({
            ...formData,
            questions: formData.questions.filter((_, i) => i !== index)
        });
    };

    const updateQuestion = (index, field, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const addOption = (questionIndex) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[questionIndex].options.push({ text: '', isCorrect: false });
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const removeOption = (questionIndex, optionIndex) => {
        const updatedQuestions = [...formData.questions];
        if (updatedQuestions[questionIndex].options.length > 1) {
            updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
            setFormData({ ...formData, questions: updatedQuestions });
        }
    };

    const updateOption = (questionIndex, optionIndex, field, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[questionIndex].options[optionIndex][field] = value;
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleImageUpload = async (e, questionIndex) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (5MB)
        if (file.size > 5000000) {
            alert('File size must be less than 5MB');
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Please select a valid image file (JPEG, JPG, PNG, GIF, WebP)');
            return;
        }

        try {
            setLoading(true);
            const uploadFormData = new FormData();
            uploadFormData.append('image', file);

            const res = await api.post('/quizzes/upload-image', uploadFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Update the question with the image URL
            updateQuestion(questionIndex, 'questionImage', res.data.imageUrl);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Error uploading image. Please try again.');
        } finally {
            setLoading(false);
            // Reset the input
            e.target.value = '';
        }
    };

    const removeQuestionImage = async (questionIndex) => {
        const question = formData.questions[questionIndex];
        if (question.questionImage) {
            try {
                // Extract filename from the image URL
                const filename = question.questionImage.split('/').pop();
                await api.delete(`/quizzes/delete-image/${filename}`);
            } catch (error) {
                console.error('Error deleting image:', error);
                // Continue anyway to remove from form
            }

            // Remove image from question
            updateQuestion(questionIndex, 'questionImage', '');
        }
    };

    // Math preview component
    const MathPreview = ({ text, className }) => {
        if (!text) return null;
        return (
            <div className={`bg-slate-50 border border-slate-200 rounded-lg p-3 ${className}`}>
                <div className="text-xs text-slate-500 mb-1">Preview:</div>
                <div className="prose prose-sm max-w-none">
                    <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                    >
                        {text}
                    </ReactMarkdown>
                </div>
            </div>
        );
    };

    if (user?.role !== 'admin') {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 rounded-3xl bg-white/70 p-12 text-center shadow-xl backdrop-blur">
                <h2 className="text-2xl font-semibold text-slate-900">Không có quyền truy cập</h2>
                <p className="text-slate-600">Bạn cần quyền quản trị viên để truy cập trang này.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-3xl p-8 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">
                            {isEditing ? 'Sửa bài tập' : 'Tạo bài tập mới'}
                        </h1>
                        <p className="text-slate-600 mt-2">
                            Tạo bài tập trắc nghiệm để học sinh luyện tập. Hỗ trợ LaTeX cho công thức toán học.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/admin')}
                        className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
                    >
                        ← Quay lại
                    </button>
                </div>

                {/* Math Support Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <div className="text-blue-600 text-xl">📐</div>
                        <div>
                            <h3 className="font-medium text-blue-900 mb-2">Hỗ trợ công thức toán học</h3>
                            <p className="text-sm text-blue-800 mb-2">
                                Sử dụng LaTeX để viết công thức toán học. Ví dụ:
                            </p>
                            <div className="bg-white rounded-lg p-3 text-sm">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="font-mono text-slate-600">{'$x^2 + y^2 = z^2$'}</div>
                                        <div>
                                            <ReactMarkdown
                                                remarkPlugins={[remarkMath]}
                                                rehypePlugins={[rehypeKatex]}
                                            >
                                                {'$x^2 + y^2 = z^2$'}
                                            </ReactMarkdown>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-mono text-slate-600">{'$$\\int_0^\\infty e^{-x} dx = 1$$'}</div>
                                        <div>
                                            <ReactMarkdown
                                                remarkPlugins={[remarkMath]}
                                                rehypePlugins={[rehypeKatex]}
                                            >
                                                {'$$\\int_0^\\infty e^{-x} dx = 1$$'}
                                            </ReactMarkdown>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Tiêu đề bài tập *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                placeholder="Nhập tiêu đề bài tập"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Thời gian (phút)</label>
                            <input
                                type="number"
                                value={formData.timeLimit}
                                onChange={(e) => setFormData({ ...formData, timeLimit: e.target.value })}
                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                placeholder="Không giới hạn"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Mô tả</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                            rows="3"
                            placeholder="Mô tả về bài tập này (hỗ trợ LaTeX)"
                        />
                        <MathPreview text={formData.description} className="mt-2" />
                    </div>

                    {/* Class Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-3">Hiển thị cho lớp *</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {classes.map((classItem) => (
                                <label key={classItem._id} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.visibleToClasses.includes(classItem._id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setFormData({
                                                    ...formData,
                                                    visibleToClasses: [...formData.visibleToClasses, classItem._id]
                                                });
                                            } else {
                                                setFormData({
                                                    ...formData,
                                                    visibleToClasses: formData.visibleToClasses.filter(id => id !== classItem._id)
                                                });
                                            }
                                        }}
                                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm font-medium text-slate-700">{classItem.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Settings */}
                    <div className="bg-slate-50 rounded-xl p-6">
                        <h3 className="font-semibold text-slate-800 mb-4">Cài đặt hiển thị kết quả</h3>
                        <div className="space-y-4">
                            <label className="flex items-start space-x-3">
                                <input
                                    type="checkbox"
                                    checked={formData.showResults}
                                    onChange={(e) => setFormData({ ...formData, showResults: e.target.checked })}
                                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 mt-1"
                                />
                                <div>
                                    <span className="text-sm font-medium text-slate-700">Hiện điểm số ngay sau khi nộp</span>
                                    <p className="text-xs text-slate-500 mt-1">Học sinh sẽ thấy điểm số ngay khi hoàn thành bài tập</p>
                                </div>
                            </label>
                            <label className="flex items-start space-x-3">
                                <input
                                    type="checkbox"
                                    checked={formData.showCorrectAnswers}
                                    onChange={(e) => setFormData({ ...formData, showCorrectAnswers: e.target.checked })}
                                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 mt-1"
                                />
                                <div>
                                    <span className="text-sm font-medium text-slate-700">Cho phép xem đáp án đúng</span>
                                    <p className="text-xs text-slate-500 mt-1">Học sinh có thể xem đáp án đúng cùng với câu trả lời của mình</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Questions */}
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-semibold text-slate-800">Câu hỏi</h3>
                            <button
                                type="button"
                                onClick={addQuestion}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
                            >
                                + Thêm câu hỏi
                            </button>
                        </div>

                        {formData.questions.map((question, qIndex) => (
                            <div key={qIndex} className="bg-white border border-slate-200 rounded-xl p-6 mb-6 shadow-sm">
                                <div className="flex items-start justify-between mb-4">
                                    <h4 className="text-lg font-semibold text-slate-700">Câu hỏi {qIndex + 1}</h4>
                                    <button
                                        type="button"
                                        onClick={() => removeQuestion(qIndex)}
                                        className="text-red-500 hover:text-red-700 p-1"
                                        disabled={formData.questions.length === 1}
                                    >
                                        🗑️ Xóa
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Nội dung câu hỏi *</label>
                                        <textarea
                                            value={question.questionText}
                                            onChange={(e) => updateQuestion(qIndex, 'questionText', e.target.value)}
                                            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                            rows="2"
                                            placeholder="Nhập câu hỏi (hỗ trợ LaTeX: $x^2$ hoặc $$\int x dx$$)"
                                            required
                                        />
                                        <MathPreview text={question.questionText} className="mt-2" />
                                    </div>

                                    {/* Question Image Upload */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Hình ảnh câu hỏi</label>
                                        <div className="space-y-3">
                                            {question.questionImage ? (
                                                <div className="relative">
                                                    <img
                                                        src={`http://localhost:5001${question.questionImage}`}
                                                        alt="Question"
                                                        className="max-w-full h-auto max-h-48 rounded-lg border border-slate-200"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeQuestionImage(qIndex)}
                                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleImageUpload(e, qIndex)}
                                                        className="hidden"
                                                        id={`image-upload-${qIndex}`}
                                                    />
                                                    <label
                                                        htmlFor={`image-upload-${qIndex}`}
                                                        className="cursor-pointer"
                                                    >
                                                        <div className="text-slate-400 mb-2 text-2xl">📷</div>
                                                        <div className="text-sm text-slate-600">
                                                            <span className="text-indigo-600 hover:text-indigo-700 font-medium">
                                                                Nhấp để tải lên hình ảnh
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-slate-400 mt-1">
                                                            PNG, JPG, GIF tối đa 5MB
                                                        </div>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Loại câu hỏi</label>
                                            <select
                                                value={question.questionType}
                                                onChange={(e) => updateQuestion(qIndex, 'questionType', e.target.value)}
                                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                            >
                                                <option value="multiple-choice">Trắc nghiệm</option>
                                                <option value="true-false">Đúng/Sai</option>
                                                <option value="short-answer">Câu trả lời ngắn</option>
                                                <option value="essay">Tự luận</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Điểm</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={question.points}
                                                onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value))}
                                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Giải thích</label>
                                            <input
                                                type="text"
                                                value={question.explanation}
                                                onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                                placeholder="Tuỳ chọn"
                                            />
                                        </div>
                                    </div>

                                    {/* Options for multiple choice and true-false */}
                                    {(question.questionType === 'multiple-choice' || question.questionType === 'true-false') && (
                                        <div>
                                            <div className="flex items-center justify-between mb-3">
                                                <label className="text-sm font-medium text-slate-700">Lựa chọn</label>
                                                {question.questionType === 'multiple-choice' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => addOption(qIndex)}
                                                        className="text-sm text-indigo-600 hover:text-indigo-700"
                                                    >
                                                        + Thêm lựa chọn
                                                    </button>
                                                )}
                                            </div>

                                            {question.questionType === 'true-false' ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {['true', 'false'].map((option) => (
                                                        <label key={option} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-xl">
                                                            <input
                                                                type="radio"
                                                                name={`correct_${qIndex}`}
                                                                checked={question.correctAnswer === option}
                                                                onChange={() => updateQuestion(qIndex, 'correctAnswer', option)}
                                                                className="text-indigo-600 focus:ring-indigo-500"
                                                            />
                                                            <span className="font-medium">
                                                                {option === 'true' ? '✓ Đúng' : '✗ Sai'}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {question.options.map((option, oIndex) => (
                                                        <div key={oIndex} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-xl">
                                                            <input
                                                                type="checkbox"
                                                                checked={option.isCorrect}
                                                                onChange={(e) => updateOption(qIndex, oIndex, 'isCorrect', e.target.checked)}
                                                                className="mt-1 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                                            />
                                                            <div className="flex-1">
                                                                <textarea
                                                                    value={option.text}
                                                                    onChange={(e) => updateOption(qIndex, oIndex, 'text', e.target.value)}
                                                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                                                                    placeholder={`Lựa chọn ${oIndex + 1} (hỗ trợ LaTeX)`}
                                                                    rows="1"
                                                                />
                                                                <MathPreview text={option.text} className="mt-2" />
                                                            </div>
                                                            {question.options.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeOption(qIndex, oIndex)}
                                                                    className="text-red-500 hover:text-red-700 p-1"
                                                                >
                                                                    🗑️
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Correct answer for short-answer */}
                                    {question.questionType === 'short-answer' && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Đáp án đúng *</label>
                                            <input
                                                type="text"
                                                value={question.correctAnswer}
                                                onChange={(e) => updateQuestion(qIndex, 'correctAnswer', e.target.value)}
                                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                                placeholder="Nhập đáp án đúng"
                                                required
                                            />
                                        </div>
                                    )}

                                    {question.explanation && (
                                        <MathPreview text={question.explanation} className="border-yellow-200 bg-yellow-50" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            className="px-6 py-3 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
                        >
                            Hủy
                        </button>
                        <button
                            type="submit"
                            disabled={loading || formData.visibleToClasses.length === 0}
                            className="px-6 py-3 text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Đang lưu...' : (isEditing ? 'Cập nhật bài tập' : 'Tạo bài tập')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default QuizCreatePage;